import { ArrowRight, Brain } from 'lucide-react';
import { Link } from 'react-router-dom';

const INSIGHT_CARDS = [
  {
    type: 'danger',
    label: 'Revenge Trading',
    cost: '$4,200 cost this quarter',
    headline: 'You traded impulsively after losses — and it showed.',
    detail: 'You entered 23 trades within 30 minutes of a loss exceeding $300. 19 of those trades closed negative. Total avoidable drawdown: $4,200.',
    borderColor: 'border-red-500/25',
    bgColor: 'bg-red-500/6',
    dotColor: 'bg-red-400',
    labelColor: 'text-red-300',
    costColor: 'text-red-400',
  },
  {
    type: 'warning',
    label: 'Sizing Drift',
    cost: '+340% larger on FOMO entries',
    headline: 'Your position size spikes when you chase moves.',
    detail: 'On days with a 1%+ gap-up open, your average position size was 3.4× your normal size. Those trades had a 28% win rate vs. 65% on normal-sized entries.',
    borderColor: 'border-amber-500/25',
    bgColor: 'bg-amber-500/6',
    dotColor: 'bg-amber-400',
    labelColor: 'text-amber-300',
    costColor: 'text-amber-400',
  },
  {
    type: 'warning',
    label: 'Time-of-Day Blindspot',
    cost: '31% win rate 9:30–10:00 AM',
    headline: 'The open is your worst hour — by a wide margin.',
    detail: 'You win 31% of trades placed in the first 30 minutes of the session. At all other times of day, your win rate is 67%. You are trading most aggressively at your worst time.',
    borderColor: 'border-blue-500/25',
    bgColor: 'bg-blue-500/6',
    dotColor: 'bg-blue-400',
    labelColor: 'text-blue-300',
    costColor: 'text-blue-400',
  },
  {
    type: 'success',
    label: 'Confirmed Edge',
    cost: 'Avg +$620 · 89% win rate',
    headline: 'NVDA long on earnings dips is your best setup.',
    detail: '8 of 9 trades on NVDA long entries within 3 days of an earnings miss were profitable, averaging $620 each. This is a documented, repeatable edge in your history.',
    borderColor: 'border-emerald-500/25',
    bgColor: 'bg-emerald-500/6',
    dotColor: 'bg-emerald-400',
    labelColor: 'text-emerald-300',
    costColor: 'text-emerald-400',
  },
];

function InsightCard({ card }) {
  return (
    <div className={`${card.bgColor} border ${card.borderColor} rounded-2xl p-6`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${card.dotColor} flex-shrink-0 mt-0.5`} />
          <span className={`text-xs font-bold uppercase tracking-widest ${card.labelColor}`}>{card.label}</span>
        </div>
        <span className={`text-xs font-bold ${card.costColor} text-right whitespace-nowrap`}>{card.cost}</span>
      </div>
      <p className="text-white font-semibold text-sm mb-2 leading-snug">{card.headline}</p>
      <p className="text-slate-400 text-xs leading-relaxed">{card.detail}</p>
    </div>
  );
}

export default function BehavioralAISection({ isAuthenticated }) {
  return (
    <div id="behavioral-ai" className="bg-[#0A1628] py-24 px-6 border-y border-slate-800/40">
      <div className="max-w-7xl mx-auto">

        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-4">
            <Brain size={13} className="text-emerald-400" />
            <span className="text-emerald-300 text-xs font-semibold tracking-widest uppercase">AI Coach</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Your trading data has a story.<br />
            <span className="text-emerald-400">Most traders never read it.</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Ask the AI Coach about your trades — it has your full history and will surface the edges and behaviors you can't see yourself.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">

          {/* Left: insight cards */}
          <div className="space-y-4">
            {INSIGHT_CARDS.map((card) => (
              <InsightCard key={card.label} card={card} />
            ))}
            <p className="text-slate-600 text-xs text-center italic pt-2">
              Sample analysis — your insights are generated from your real trade history, not generic advice
            </p>
          </div>

          {/* Right: explanation */}
          <div>
            <div className="bg-slate-800/30 border border-slate-700/40 rounded-2xl p-8 mb-8">
              <blockquote className="text-xl text-slate-200 italic leading-relaxed mb-4">
                "I could see I was losing — I just couldn't see <em>why</em>. The behavioral analysis showed me in 30 seconds what three months of journaling couldn't."
              </blockquote>
              <p className="text-emerald-400 font-semibold text-sm">— What the AI Coach surfaces for every trader</p>
            </div>

            <div className="space-y-4 mb-8">
              {[
                { label: 'Time-of-day profiling', desc: 'Exact win rate by hour of day — statistically computed from your closed trades.' },
                { label: 'Symbol edge scoring', desc: 'Which tickers you actually win on vs. which are draining you.' },
                { label: 'Session analysis', desc: 'Pre-market, regular, after-hours — your win rate differs and the data shows it.' },
                { label: 'Behavioral patterns', desc: 'Ask the AI Coach — it will find revenge trading, sizing drift, and FOMO entries in your history.' },
                { label: 'Confirmed edges', desc: 'The specific setups with a real statistical edge. Build playbooks around them.' },
              ].map(({ label, desc }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  </div>
                  <div>
                    <span className="text-white text-sm font-medium">{label} — </span>
                    <span className="text-slate-400 text-sm">{desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5 mb-6">
              <p className="text-emerald-300 text-sm font-medium mb-1">Privacy-first AI</p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Run AI analysis locally via Ollama or LM Studio — your trade data never leaves your machine. Or use cloud providers with your own API key.
              </p>
            </div>

            {isAuthenticated ? (
              <Link
                to="/strategy"
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-6 py-3 rounded-xl transition-colors w-fit"
              >
                Open AI Coach <ArrowRight size={16} />
              </Link>
            ) : (
              <Link
                to="/login?mode=register"
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-6 py-3 rounded-xl transition-colors w-fit"
              >
                Start Free — Edge Analysis Included <ArrowRight size={16} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
